import { z } from 'zod'

import { ensureRouteInfiniteData } from '@/app/query-client'
import { companyListQueryOptions } from '@/features/company/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { searchTextParamSchema } from '@/schemas/common'

/**
 * Search params are validated at the route, so `search` and `sector` are typed
 * everywhere downstream and a malformed URL falls back to a valid state
 * instead of propagating `undefined` into a query key.
 *
 * Here rather than in CompaniesScreen.tsx because the loader below reads the
 * filters through it too, and the route's `validateSearch` and the loader
 * must not be able to disagree about which list a URL means.
 */
export const companiesSearchSchema = z.object({
  search: searchTextParamSchema.optional(),
  sector: z.string().optional(),
})

/**
 * The `/companies` loader.
 *
 * MAIN: the first page of the list the URL's filters name, awaited — the
 * table and its count are the page. No secondary reads.
 *
 * The filters are read from `location.search` through the route's own
 * schema, not through `loaderDeps`: a dep would make every debounced
 * keystroke in the search box a new route match — the full-page pending
 * skeleton while typing. As it is, a search runs no loader at all; the
 * screen keeps the previous rows on screen, dimmed, until the next land.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own. It imports from `api/` modules and schemas only — route
 * loaders are not code-split, so anything imported here ships in the entry
 * chunk.
 */
export async function loadCompanies({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const { search, sector } = companiesSearchSchema.parse(location.search)
  await ensureRouteInfiniteData(queryClient, companyListQueryOptions({ search, sector }))
}
